// HTML文字列を組み立てて innerHTML に流す箇所で使う、値のエスケープ／無害化ヘルパー。
//
// icons.js のように「テンプレートリテラルでHTMLを組んで innerHTML に入れる」実装では、
// 補間する値に `"` や `'` が含まれると属性を抜け出して任意の属性・タグを注入できる。
// キャンペーン名やメディアURLは従来 storeeel 側で発行・検証された値だけだったが、
// 連携配信（フィードフォルダ）の導入で GMC 由来の商品タイトル・商品画像URLが
// 同じ経路に流れるようになったため、外部データ前提でエスケープを通す。

// HTML本文・属性値の両方に使える基本エスケープ。
// 属性は必ずダブルクォートで囲うこと（無囲みだと空白で属性を追加できてしまう）。
export const escapeHtml = (value) => String(value ?? '')
  .replaceAll('&', '&amp;')
  .replaceAll('<', '&lt;')
  .replaceAll('>', '&gt;')
  .replaceAll('"', '&quot;')
  .replaceAll("'", '&#39;');

// スクリプトを実行しうるURLスキーム
const DANGEROUS_URL_SCHEME = /^(?:javascript|data|vbscript):/i;

// スキーム判定の前に空白・制御文字を落とす。`java&#9;script:` のように
// タブや改行を挟んでスキームを偽装する手口があるため。
// 文字クラスに制御文字を直接書くとソース上で見えなくなるので、コードポイントで判定する。
const stripUrlSchemeNoise = (value) => Array.from(value)
  .filter((char) => char.charCodeAt(0) > 0x20)
  .join('');

// メディアURLとして受け入れてよい値だけを返す。危険なスキームは空文字に落とす。
// 空文字にすると画像・動画が出ないだけで、描画自体は壊れない。
export const sanitizeUrl = (value) => {
  const raw = String(value ?? '');
  return DANGEROUS_URL_SCHEME.test(stripUrlSchemeNoise(raw)) ? '' : raw;
};

// `src="..."` や `data-...="..."` のようにURLを属性値として埋め込むとき用。
export const escapeUrlAttribute = (value) => escapeHtml(sanitizeUrl(value));

// `url()` の内側で「宣言を抜け出す」ために使える文字だけをパーセントエンコードする。
// URL全体を encodeURI にかけてはいけない（`%` も再エンコードされるため、
// すでにパーセントエンコード済みの商品画像URLが二重エンコードされて画像が出なくなる）。
const CSS_URL_ESCAPES = {
  "'": '%27',
  '"': '%22',
  '(': '%28',
  ')': '%29',
  '\\': '%5C',
};

// `background-image: url('...')` の内側に埋め込むとき用。
// 属性エスケープだけでは足りない（`'` や `)` でCSSの宣言を抜けられる）。
export const escapeCssUrl = (value) => {
  const safe = sanitizeUrl(value);
  if (!safe) return '';

  return Array.from(safe)
    .map((char) => {
      // 空白・制御文字は url() を壊すためエンコードする
      if (char.charCodeAt(0) <= 0x20) return encodeURIComponent(char);
      return CSS_URL_ESCAPES[char] ?? char;
    })
    .join('');
};
